import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Card,
    CardContent,
    Divider,
    Collapse,
    Stack,
    Grid,
    Tooltip
} from '@mui/material';
import {
    Translate as TranslateIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Language as LanguageIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

import { findLangKey } from '../../../helper/i18n'

// ============================================================================
// REUSABLE COMPONENT: MultiLangLabel (Native MUI v5)
// ============================================================================
/**
 * MultiLangLabel Component
 * Displays only the current language's translation value first, with an
 * interactive toggle button to expand and show all other languages inline.
 * Utilizes native MUI components, Collapse transitions, and theme-safe colors.
 * * @param {Object} label - The language object e.g., { en: 'Hello', 'zh-HK': '你好' }
 * @param {string} currentLang - The language key to display first (e.g., 'en', 'zh-HK', 'zh-CN')
 */
export default function MultiLangLabelV2({ label, currentLang = 'en' }) {
    const [isExpanded, setIsExpanded] = useState(false);

    const curlang = currentLang.toLowerCase()

    const targetKey = findLangKey(curlang, label);

    if (!label || typeof label !== 'object') {
        return (
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            No translations
        </Typography>
        );
    }

    // Visual language themes mapping
    const getLangMeta = (lang) => {
        const meta = {
        en: { short: 'EN', bg: 'blue.50', color: 'blue.800', border: 'blue.200' },
        'zh-HK': { short: 'TC', bg: 'success.shades?.50' || '#e8f5e9', color: 'success.dark', border: 'success.light' },
        'zh-CN': { short: 'SC', bg: 'secondary.light', color: 'secondary.dark', border: 'secondary.light' }
        };
        return meta[lang] || { short: lang.toUpperCase(), bg: 'grey.100', color: 'text.primary', border: 'grey.300' };
    };

    const allLangKeys = Object.keys(label);
    const otherLangKeys = allLangKeys.filter(lang => lang.toLowerCase() !== curlang);
    const hasOthers = otherLangKeys.length > 0;

    const primaryValue = label[targetKey] || label[curlang.toUpperCase()];
    const primaryMeta = getLangMeta(curlang);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, 
                width: 'fit-content' }}>
        {/* Primary Language Row */}
        <Stack direction="column" alignItems="left" spacing={0} flexWrap="wrap" useFlexGap>
            {/* Customized Chip layout using real MUI Box styles */}
            <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                bgcolor: primaryMeta.bg === 'secondary.light' ? '#f3e5f5' : primaryMeta.bg,
                color: primaryMeta.color,
                // border: '1px solid',
                borderColor: primaryMeta.border === 'secondary.light' ? '#e1bee7' : primaryMeta.border,
                borderRadius: 1,
                px: 1,
                pt: 0.5,
                fontSize: '0.8125rem',
                fontWeight: 500,
            }}
            >
            {/* <Typography
                component="span"
                sx={{
                fontSize: '0.625rem',
                fontWeight: 800,
                letterSpacing: '0.05em',
                borderRight: '1px solid currentColor',
                pr: 1,
                mr: 1,
                opacity: 0.7,
                lineHeight: 1
                }}
            >
                {primaryMeta.short}
            </Typography> */}
            <Typography
                component="span"
                variant="body2"
                sx={{
                fontWeight: 600,
                maxWidth: '200px',
                overflow: 'hidden',
                // textOverflow: 'ellipsis',
                whiteSpace: 'wrap'
                }}
                title={primaryValue}
            >
                {primaryValue || (
                <Box component="span" sx={{ color: 'error.main', fontStyle: 'italic', fontWeight: 400 }}>
                    Empty
                </Box>
                )}
            </Typography>
            </Box>

            {/* Toggle Button for Other Languages */}
            {/* {hasOthers && (
            <Button
                size="small"
                variant="outlined"
                onClick={() => setIsExpanded(!isExpanded)}
                // endIcon={isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                endIcon={isExpanded ? <ExpandLessIcon fontSize="small" /> : null}
                sx={{
                fontSize: '0.6875rem',
                pb: 0.25,
                px: 1,
                minWidth: 'auto',
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 700,
                color: isExpanded ? 'common.white' : 'text.secondary',
                bgcolor: isExpanded ? 'grey.800' : 'background.paper',
                border: 'none',
                borderColor: isExpanded ? 'grey.300' : 'grey.300',
                '&:hover': {
                    bgcolor: isExpanded ? 'grey.900' : 'grey.100',
                    border: 'none',
                    borderColor: isExpanded ? 'grey.900' : 'grey.400',
                }
                }}
            >
                {isExpanded ? 'Collapse' : `...`}
            </Button>
            )} */}
        </Stack>

        {/* Expanded Language Rows using native MUI Collapse component */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                pl: 1.5,
                ml: 0.5,
                borderLeft: '2px solid',
                borderColor: 'grey.300',
                py: 0.5,
                borderRadius: '0 4px 4px 0'
            }}
            >
            {otherLangKeys.map((lang) => {
                const val = label[lang];
                const meta = getLangMeta(lang);

                return (
                <Box
                    key={lang}
                    sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    bgcolor: meta.bg === 'secondary.light' ? '#f3e5f5' : meta.bg,
                    color: meta.color,
                    border: '1px solid',
                    borderColor: meta.border === 'secondary.light' ? '#e1bee7' : meta.border,
                    borderRadius: 1,
                    px: 1,
                    py: 0.25,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    }}
                >
                    <Typography
                    component="span"
                    sx={{
                        fontSize: '0.5625rem',
                        fontWeight: 800,
                        letterSpacing: '0.05em',
                        borderRight: '1px solid currentColor',
                        pr: 0.75,
                        mr: 0.75,
                        opacity: 0.7,
                        lineHeight: 1
                    }}
                    >
                    {meta.short}
                    </Typography>
                    <Typography
                    component="span"
                    variant="caption"
                    sx={{
                        fontWeight: 500,
                        maxWidth: '150px',
                        overflow: 'hidden',
                        // textOverflow: 'ellipsis',
                        whiteSpace: 'wrap'
                    }}
                    title={val}
                    >
                    {val || (
                        <Box component="span" sx={{ color: 'error.main', fontStyle: 'italic', fontWeight: 400 }}>
                        Empty
                        </Box>
                    )}
                    </Typography>
                </Box>
                );
            })}
            </Box>
        </Collapse>
        </Box>
    );
};